// FILE: src/pages/AddUserFormPage.jsx
// Add (invite) a student (plan WP-8, B-26, matrix 0.6 / 9.x). The licence
// class is G1, G2 or "No licence yet" (owner decision D11), with no default.
// With G1/G2 the licence number is required and checked in the same shape
// the server checks; with "No licence yet" the number field is hidden and
// sent empty. Every failure is shown in words next to the field it concerns
// and in an alert, never as raw JSON.
// WP-6: the account is created not enrolled; the one-year enrolment starts
// when the student registers. When the account was created but the email
// failed (502 INVITE_EMAIL_FAILED { user }), the page says so and offers to
// send it again (POST /api/admin/users/:id/resend-invite).
import React, { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import { Alert, AlertTitle, Box, Button, Card, CardContent, MenuItem, Stack, TextField, Typography } from "@mui/material";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import { addUser } from "../features/adminUsersSlice";
import fetchWithAuth from "../utils/fetchWithAuth";
import { errorPayload } from "../components/admin/apiError";
import { PageHeader, Section } from "../components/ui";
import { errorCode, errorMessage } from "../i18n/errors";
import { licenceClassLabel } from "../i18n/labels";
import {
  FIELD_FOR_CODE,
  LICENCE_CLASSES,
  LICENCE_HINT,
  emailError,
  licenceClassError,
  licenceNumberError,
  needsLicenceNumber,
  normaliseLicence
} from "../components/admin/studentForm";

const EMPTY = {
  firstName: "",
  middleName: "",
  lastName: "",
  email: "",
  phone: "",
  licenseClass: "",
  licenseNumber: ""
};

// Field order, for moving focus to the first problem.
const ORDER = ["firstName", "lastName", "email", "phone", "licenseClass", "licenseNumber"];

function validateStudent(form) {
  const errors = {};
  if (!form.firstName.trim()) errors.firstName = "Enter the student's first name.";
  if (!form.lastName.trim()) errors.lastName = "Enter the student's last name.";
  const email = emailError(form.email);
  if (email) errors.email = email;
  if (!form.phone.trim()) errors.phone = "Enter a phone number we can reach the student on.";
  const cls = licenceClassError(form.licenseClass);
  if (cls) errors.licenseClass = cls;
  const number = licenceNumberError(form.licenseNumber, form.licenseClass);
  if (number) errors.licenseNumber = number;
  return errors;
}

export default function AddUserFormPage() {
  const dispatch = useDispatch();
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [invited, setInvited] = useState(null);
  const [emailFailed, setEmailFailed] = useState(null); // { user, email, firstName }
  const [resending, setResending] = useState(false);
  const [resendError, setResendError] = useState(null);
  const refs = useRef({});
  const successRef = useRef(null);
  const alertRef = useRef(null);

  const register = (name) => (el) => {
    refs.current[name] = el;
  };

  const focusFirst = (errs) => {
    const first = ORDER.find((f) => errs[f]);
    if (first && refs.current[first]) refs.current[first].focus();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    // "No licence yet" has no number: clear it (the field is hidden).
    const clearNumber = name === "licenseClass" && !needsLicenceNumber(value);
    setForm((prev) => ({ ...prev, [name]: value, ...(clearNumber ? { licenseNumber: "" } : {}) }));
    if (errors[name] || (clearNumber && errors.licenseNumber)) {
      setErrors((prev) => ({ ...prev, [name]: undefined, ...(clearNumber ? { licenseNumber: undefined } : {}) }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError(null);
    const found = validateStudent(form);
    setErrors(found);
    if (Object.keys(found).length) {
      focusFirst(found);
      return;
    }
    setSubmitting(true);
    const payload = {
      firstName: form.firstName.trim(),
      middleName: form.middleName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      licenseClass: form.licenseClass,
      licenseNumber: needsLicenceNumber(form.licenseClass) ? normaliseLicence(form.licenseNumber) : ""
    };
    try {
      await dispatch(addUser(payload)).unwrap();
      setInvited({ email: payload.email, firstName: payload.firstName });
      setForm(EMPTY);
      setErrors({});
      setTimeout(() => successRef.current?.focus(), 0);
    } catch (err) {
      const code = errorCode(err);
      if (code === "INVITE_EMAIL_FAILED") {
        // The account exists; only the email failed.
        setEmailFailed({ user: errorPayload(err).user || null, email: payload.email, firstName: payload.firstName });
        setResendError(null);
        setForm(EMPTY);
        setErrors({});
        setTimeout(() => successRef.current?.focus(), 0);
        return;
      }
      const field = FIELD_FOR_CODE[code];
      setServerError(err || "INTERNAL_ERROR");
      if (field) {
        setErrors((prev) => ({ ...prev, [field]: errorMessage(err) }));
        setTimeout(() => refs.current[field]?.focus(), 0);
      } else {
        setTimeout(() => alertRef.current?.focus(), 0);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const resend = async () => {
    const id = emailFailed?.user?._id;
    if (!id) return;
    setResending(true);
    setResendError(null);
    try {
      await fetchWithAuth(`/api/admin/users/${id}/resend-invite`, { method: "POST" });
      setInvited({ email: emailFailed.email, firstName: emailFailed.firstName });
      setEmailFailed(null);
      setTimeout(() => successRef.current?.focus(), 0);
    } catch (err) {
      setResendError(err || "INTERNAL_ERROR");
    } finally {
      setResending(false);
    }
  };

  const addAnother = () => {
    setInvited(null);
    setEmailFailed(null);
    setServerError(null);
    setTimeout(() => refs.current.firstName?.focus(), 0);
  };

  const breadcrumbs = [{ label: "Students", to: "/admin/users" }, { label: "Add student" }];

  if (emailFailed) {
    return (
      <Box>
        <PageHeader title="Add student" breadcrumbs={breadcrumbs} />
        <Card sx={{ maxWidth: 720, bgcolor: "zun.warningTint", borderColor: "transparent" }}>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", mb: 1.5 }}>
              <WarningAmberOutlinedIcon aria-hidden sx={{ color: "warning.main", fontSize: 32 }} />
              <Typography
                ref={successRef}
                tabIndex={-1}
                variant="h3"
                component="h2"
                sx={{ overflowWrap: "anywhere", outline: "none" }}
              >
                Account created, but the invitation email couldn't be sent
              </Typography>
            </Stack>
            <Typography sx={{ mb: 3, overflowWrap: "anywhere" }}>
              The account for {emailFailed.email} exists, so don't add it again.{" "}
              {emailFailed.user?._id
                ? "Send the invitation again now, or later from the student's page."
                : "Send the invitation again from the student's page in the student list."}
            </Typography>
            {resendError && (
              <Alert severity="error" role="alert" sx={{ mb: 2 }}>
                {errorMessage(resendError)}
              </Alert>
            )}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              {emailFailed.user?._id && (
                <Button variant="contained" startIcon={<SendOutlinedIcon />} onClick={resend} disabled={resending}>
                  {resending ? "Sending the invitation…" : "Send the invitation again"}
                </Button>
              )}
              <Button component={RouterLink} to="/admin/users" variant="outlined">
                Back to students
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (invited) {
    return (
      <Box>
        <PageHeader title="Add student" breadcrumbs={breadcrumbs} />
        <Card sx={{ maxWidth: 720, bgcolor: "zun.primaryTint", borderColor: "transparent" }}>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", mb: 1.5 }}>
              <MarkEmailReadOutlinedIcon aria-hidden sx={{ color: "primary.main", fontSize: 32 }} />
              <Typography
                ref={successRef}
                tabIndex={-1}
                variant="h3"
                component="h2"
                sx={{ overflowWrap: "anywhere", outline: "none" }}
              >
                Invitation sent to {invited.email}
              </Typography>
            </Stack>
            <Typography sx={{ mb: 3 }}>
              We emailed {invited.firstName || "the student"} a link to finish registering. Until they do, they appear in the
              student list as “Invitation sent”. Their one-year enrolment starts when they register.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <Button variant="contained" startIcon={<PersonAddAlt1OutlinedIcon />} onClick={addAnother}>
                Add another
              </Button>
              <Button component={RouterLink} to="/admin/users" variant="outlined">
                Back to students
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Add student"
        breadcrumbs={breadcrumbs}
        description="We'll email the student an invitation to register. Students can be invited before they get their G1."
      />

      <Box component="form" noValidate onSubmit={handleSubmit} aria-label="Add student" sx={{ maxWidth: 720 }}>
        <Stack spacing={2.5}>
          {serverError && (
            <Alert severity="error" ref={alertRef} tabIndex={-1} role="alert">
              <AlertTitle>We couldn't send the invitation</AlertTitle>
              {errorMessage(serverError)}
            </Alert>
          )}

          <Section id="personal" title="Personal details" description="As on the student's licence.">
            <Stack spacing={2.5}>
              <Box sx={{ display: "grid", gap: 2.5, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
                <TextField
                  id="firstName"
                  name="firstName"
                  label="First name"
                  value={form.firstName}
                  onChange={handleChange}
                  inputRef={register("firstName")}
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  autoComplete="off"
                  required
                  fullWidth
                />
                <TextField
                  id="middleName"
                  name="middleName"
                  label="Middle name"
                  value={form.middleName}
                  onChange={handleChange}
                  helperText="Optional"
                  autoComplete="off"
                  fullWidth
                />
              </Box>
              <TextField
                id="lastName"
                name="lastName"
                label="Last name"
                value={form.lastName}
                onChange={handleChange}
                inputRef={register("lastName")}
                error={!!errors.lastName}
                helperText={errors.lastName}
                autoComplete="off"
                required
                fullWidth
              />
              <TextField
                id="email"
                name="email"
                type="email"
                label="Email"
                value={form.email}
                onChange={handleChange}
                inputRef={register("email")}
                error={!!errors.email}
                helperText={errors.email || "The invitation is sent here."}
                autoComplete="off"
                required
                fullWidth
              />
              <TextField
                id="phone"
                name="phone"
                type="tel"
                label="Phone"
                value={form.phone}
                onChange={handleChange}
                inputRef={register("phone")}
                error={!!errors.phone}
                helperText={errors.phone}
                autoComplete="off"
                required
                fullWidth
              />
            </Stack>
          </Section>

          <Section
            id="licence"
            title="Licence"
            description="Choose G1 or G2 and enter the licence number, or choose No licence yet if the student doesn't have a licence."
          >
            <Box sx={{ display: "grid", gap: 2.5, gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) minmax(0, 1.4fr)" } }}>
              <TextField
                select
                id="licenseClass"
                name="licenseClass"
                label="Licence class"
                value={form.licenseClass}
                onChange={handleChange}
                inputRef={register("licenseClass")}
                error={!!errors.licenseClass}
                helperText={errors.licenseClass}
                required
                fullWidth
              >
                {LICENCE_CLASSES.map((c) => (
                  <MenuItem key={c} value={c}>
                    {licenceClassLabel(c)}
                  </MenuItem>
                ))}
              </TextField>
              {needsLicenceNumber(form.licenseClass) && (
                <TextField
                  id="licenseNumber"
                  name="licenseNumber"
                  label="Licence number"
                  value={form.licenseNumber}
                  onChange={handleChange}
                  onBlur={() => setForm((prev) => ({ ...prev, licenseNumber: normaliseLicence(prev.licenseNumber) }))}
                  inputRef={register("licenseNumber")}
                  error={!!errors.licenseNumber}
                  helperText={errors.licenseNumber || LICENCE_HINT}
                  autoComplete="off"
                  slotProps={{ htmlInput: { spellCheck: false, autoCapitalize: "characters" } }}
                  required
                  fullWidth
                />
              )}
            </Box>
          </Section>

          <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5} sx={{ justifyContent: "flex-end" }}>
            <Button component={RouterLink} to="/admin/users" variant="outlined">
              Cancel
            </Button>
            <Button type="submit" variant="contained" startIcon={<SendOutlinedIcon />} disabled={submitting}>
              {submitting ? "Sending invitation…" : "Send invitation"}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}
